import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Image as ImageIcon, UploadCloud, Trash2, Sparkles,
  Loader2, X, ChevronLeft, ChevronRight, Tag, FileType2,
  Boxes, Camera, Lightbulb, Palette, Sofa, Layers as LayersIcon,
  Star, Wand2,
} from 'lucide-react';
import { HudCard, HudButton } from '../hud';
import ApartmentPlanCard from './ApartmentPlanCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Doc-type metadata for apartment_single projects.
 *  - PLANS bucket → already handled by ApartmentPlanCard on Dashboard (we
 *    just link there).
 *  - PHOTOS bucket → grid + lightbox (etat_actuel, inspiration, photo)
 *  - DOCS bucket → list + per-row Analyser CTA (cctp, ref_materiau,
 *    ref_mobilier, piece_ecrite, other)
 */
const CATEGORIES = [
  { key: 'etat_actuel',  label: 'État actuel',     icon: Camera,    bucket: 'photo', tone: '#94A3B8',
    hint: "Photos du bien tel qu'il est aujourd'hui (peuvent alimenter la matériauthèque)." },
  { key: 'inspiration',  label: 'Inspiration',     icon: Lightbulb, bucket: 'photo', tone: '#FBBF24',
    hint: 'Photos d\'ambiance / mood-board que vous souhaitez répliquer.' },
  { key: 'photo',        label: 'Photo (autre)',   icon: ImageIcon, bucket: 'photo', tone: '#A78BFA' },
  { key: 'plan_lot',     label: 'Plan',            icon: LayersIcon,bucket: 'doc',   tone: '#60A5FA',
    hint: 'Plans 2D — le plan principal est édité depuis le Dashboard.' },
  { key: 'ref_visuel',   label: 'Réf. générateur IA', icon: Sparkles, bucket: 'doc',   tone: '#D9A05B',
    hint: 'Image de référence transmise au générateur 3D (porte, cuisine, ambiance…).' },
  { key: 'cctp',         label: 'CCTP / Cahier',   icon: FileText,  bucket: 'doc',   tone: '#34D399',
    hint: 'CCTP du chantier — analysable en matériauthèque.' },
  { key: 'ref_materiau', label: 'Réf. matériau',   icon: Palette,   bucket: 'doc',   tone: '#F472B6',
    hint: 'Échantillons / fiches techniques de matériaux à intégrer.' },
  { key: 'ref_mobilier', label: 'Réf. mobilier',   icon: Sofa,      bucket: 'doc',   tone: '#FB923C',
    hint: 'Mobilier de référence (catalogue, croquis).' },
  { key: 'piece_ecrite', label: 'Pièce écrite',    icon: FileType2, bucket: 'doc',   tone: '#64748B' },
  { key: 'other',        label: 'Autre',           icon: Boxes,     bucket: 'doc',   tone: '#94A3B8' },
];

const CAT_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

const ApartmentDossierSection = ({ projectId, project, files, setFiles, navigate, pendingForBadge, onProjectSaved }) => {
  return (
    <div className="hud-stagger space-y-6" data-testid="apartment-dossier-section">
      {/* Headline + materialtheque CTA */}
      <HudCard variant="glass" bracket>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Dossier appartement</span>
            <p className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 22 }}>
              Mon appartement
            </p>
            <p className="text-[12.5px] mt-1.5 leading-relaxed max-w-3xl"
              style={{ color: 'var(--c-text-muted)' }}>
              Centralisez les plans, photos et documents qui décrivent votre bien. Chaque élément
              peut être catégorisé et analysé à la demande (matériauthèque IA, CCTP, état des lieux…).
            </p>
          </div>
          <HudButton
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/materialtheque?project=${projectId}`)}
            leftIcon={<Boxes className="w-3.5 h-3.5" />}
            data-testid="apartment-open-materialtheque"
          >
            Matériauthèque IA
            {pendingForBadge && <span aria-hidden className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: '#FBBF24', boxShadow: '0 0 6px rgba(251,191,36,0.7)' }} />}
          </HudButton>
        </div>
      </HudCard>

      <ApartmentPlanCard projectId={projectId} project={project}
        onConfirmed={(gi) => onProjectSaved?.({ general_info: gi })} />

      <PhotosCardImpl projectId={projectId} files={files} setFiles={setFiles} />
      <DocsCardImpl projectId={projectId} files={files} setFiles={setFiles} navigate={navigate} />
    </div>
  );
};

// ==================== PHOTOS BUCKET ====================

const PhotosCardImpl = ({ projectId, files: allFiles, setFiles }) => {
  const files = (allFiles || []).filter((f) => ['etat_actuel', 'inspiration', 'photo'].includes(f.doc_type)
    || (f.content_type?.startsWith('image/') && !f.doc_type));
  const [uploading, setUploading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [pendingCat, setPendingCat] = useState('etat_actuel');
  const inputRef = useRef(null);

  const handleUpload = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const f of Array.from(fileList)) {
      try {
        const form = new FormData();
        form.append('file', f);
        const r = await axios.post(`${API}/projects/${projectId}/files`, form, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        // Apply the chosen category right away
        const upd = await axios.put(`${API}/files/${r.data.file_id}`, {
          doc_type: pendingCat,
        }, { withCredentials: true });
        uploaded.push(upd.data);
      } catch (e) {
        toast.error(`Échec upload ${f.name}`);
      }
    }
    if (uploaded.length) {
      setFiles((prev) => [...uploaded, ...prev]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? 's' : ''} uploadée${uploaded.length > 1 ? 's' : ''}`);
    }
    setUploading(false);
  }, [projectId, pendingCat, setFiles]);

  const handleDelete = useCallback(async (fileId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await axios.delete(`${API}/files/${fileId}`, { withCredentials: true });
      setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
      toast.success('Photo supprimée');
    } catch { toast.error('Erreur suppression'); }
  }, [setFiles]);

  const handleChangeCat = useCallback(async (fileId, newCat) => {
    try {
      const r = await axios.put(`${API}/files/${fileId}`, { doc_type: newCat }, { withCredentials: true });
      setFiles((prev) => prev.map((f) => f.file_id === fileId ? r.data : f));
    } catch { toast.error('Erreur catégorisation'); }
  }, [setFiles]);

  const handleToggleStar = useCallback(async (fileId, current) => {
    try {
      const r = await axios.put(`${API}/files/${fileId}`,
        { is_retained: !current }, { withCredentials: true });
      setFiles((prev) => prev.map((f) => f.file_id === fileId ? r.data : f));
      toast.success(!current
        ? "Étoilée ⭐ — sera injectée dans la prochaine génération 3D (max 5)"
        : "Retirée de la sélection génération");
    } catch { toast.error("Erreur étoile"); }
  }, [setFiles]);

  return (
    <HudCard variant="glass" bracket data-testid="apartment-photos-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">Photos de référence</span>
          <p className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}>
            {files.length} photo{files.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Category for next upload */}
          <div className="flex items-center gap-1 text-[11px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
            <Tag className="w-3 h-3" /> Catégorie suivante :
          </div>
          <select
            value={pendingCat}
            onChange={(e) => setPendingCat(e.target.value)}
            data-testid="apartment-photos-cat-select"
            className="h-8 px-2 rounded-md bg-white/5 border border-white/15 text-[12px] text-white"
          >
            {CATEGORIES.filter((c) => c.bucket === 'photo').map((c) => (
              <option key={c.key} value={c.key} style={{ background: '#1a1a1f' }}>{c.label}</option>
            ))}
          </select>
          <input ref={inputRef} type="file" multiple
            accept="image/*"
            className="hidden"
            data-testid="apartment-photos-input"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="apartment-photos-upload-btn"
            className="text-[11.5px] px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, #D9A05B 0%, #B07A35 100%)',
              color: '#0E0E10', fontWeight: 600,
              boxShadow: '0 4px 14px -4px rgba(217,160,91,0.45)',
            }}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
            Uploader
          </button>
        </div>
      </div>

      {files.length === 0 ? (
        <Dropzone onFiles={handleUpload} accept="image/*"
          icon={<Camera className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.40)' }} />}
          label="Glissez vos photos ici ou cliquez sur Uploader"
          sub="État actuel, inspiration, ou photos diverses — JPG/PNG/WEBP"
          testid="apartment-photos-dropzone"
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="apartment-photos-grid">
          {files.map((f, idx) => {
            const cat = CAT_BY_KEY[f.doc_type] || CAT_BY_KEY.photo;
            return (
              <div key={f.file_id}
                data-testid={`apartment-photo-thumb-${f.file_id}`}
                className="group relative rounded-lg overflow-hidden border border-white/10 bg-black/30 aspect-square cursor-pointer hover:border-[#D9A05B]/60 transition-colors"
                onClick={() => setLightboxIdx(idx)}
              >
                <img
                  src={`${API}/files/${f.file_id}/download`}
                  alt={f.original_filename || ''}
                  className="w-full h-full object-cover"
                  crossOrigin="use-credentials"
                />
                {/* Category badge */}
                <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9.5px] font-semibold inline-flex items-center gap-1 backdrop-blur-md"
                  style={{ background: 'rgba(0,0,0,0.55)', color: cat.tone, border: `1px solid ${cat.tone}55` }}>
                  <cat.icon className="w-2.5 h-2.5" /> {cat.label}
                </div>
                {/* Star toggle — shortlists this image for 3D generation */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleToggleStar(f.file_id, !!f.is_retained); }}
                  data-testid={`apartment-photo-star-${f.file_id}`}
                  title={f.is_retained
                    ? "Étoilée ⭐ — injectée dans les rendus 3D (max 5 plus récentes par projet). Cliquer pour retirer."
                    : "Étoiler ⭐ — sera injectée comme image de référence dans la prochaine génération 3D"}
                  className={`absolute bottom-1.5 left-1.5 p-1 rounded transition-opacity ${
                    f.is_retained ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                  style={{
                    background: f.is_retained ? 'rgba(217,160,91,0.85)' : 'rgba(0,0,0,0.55)',
                    color: f.is_retained ? '#1a1a1f' : '#D9A05B',
                    border: '1px solid rgba(217,160,91,0.55)',
                  }}
                >
                  <Star className="w-3 h-3" fill={f.is_retained ? 'currentColor' : 'none'} />
                </button>
                {/* Delete */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(f.file_id); }}
                  data-testid={`apartment-photo-delete-${f.file_id}`}
                  className="absolute top-1.5 right-1.5 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: 'rgba(220,38,38,0.85)', color: 'white' }}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {lightboxIdx !== null && (
        <Lightbox
          files={files}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => (i - 1 + files.length) % files.length)}
          onNext={() => setLightboxIdx((i) => (i + 1) % files.length)}
          onChangeCat={handleChangeCat}
        />
      )}
    </HudCard>
  );
};

// ==================== DOCS BUCKET ====================

const DocsCardImpl = ({ projectId, files: allFiles, setFiles, navigate }) => {
  const files = (allFiles || []).filter((f) => ['plan_lot', 'cctp', 'ref_materiau', 'ref_mobilier', 'ref_visuel', 'piece_ecrite', 'other'].includes(f.doc_type)
    || (!f.content_type?.startsWith('image/') && !f.doc_type));
  const [uploading, setUploading] = useState(false);
  const [pendingCat, setPendingCat] = useState('cctp');
  const [analyzingId, setAnalyzingId] = useState(null);
  const inputRef = useRef(null);

  const handleUpload = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const f of Array.from(fileList)) {
      try {
        const form = new FormData();
        form.append('file', f);
        const r = await axios.post(`${API}/projects/${projectId}/files`, form, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const upd = await axios.put(`${API}/files/${r.data.file_id}`, {
          doc_type: pendingCat,
        }, { withCredentials: true });
        uploaded.push(upd.data);
      } catch (e) {
        toast.error(`Échec upload ${f.name}`);
      }
    }
    if (uploaded.length) {
      setFiles((prev) => [...uploaded, ...prev]);
      toast.success(`${uploaded.length} document${uploaded.length > 1 ? 's' : ''} uploadé${uploaded.length > 1 ? 's' : ''}`);
    }
    setUploading(false);
  }, [projectId, pendingCat, setFiles]);

  const handleDelete = useCallback(async (fileId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API}/files/${fileId}`, { withCredentials: true });
      setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
      toast.success('Document supprimé');
    } catch { toast.error('Erreur suppression'); }
  }, [setFiles]);

  const handleChangeCat = useCallback(async (fileId, newCat) => {
    try {
      const r = await axios.put(`${API}/files/${fileId}`, { doc_type: newCat }, { withCredentials: true });
      setFiles((prev) => prev.map((f) => f.file_id === fileId ? r.data : f));
    } catch { toast.error('Erreur catégorisation'); }
  }, [setFiles]);

  const handleToggleStar = useCallback(async (fileId, current) => {
    try {
      const r = await axios.put(`${API}/files/${fileId}`,
        { is_retained: !current }, { withCredentials: true });
      setFiles((prev) => prev.map((f) => f.file_id === fileId ? r.data : f));
      toast.success(!current
        ? "Étoilée ⭐ — sera injectée dans la prochaine génération 3D (max 5)"
        : "Retirée de la sélection génération");
    } catch { toast.error("Erreur étoile"); }
  }, [setFiles]);

  const handleAnalyze = useCallback(async (f) => {
    setAnalyzingId(f.file_id);
    try {
      const ct = (f.content_type || '').toLowerCase();
      const isImage = ct.startsWith('image/');
      const isVisualRef = ['etat_actuel', 'inspiration', 'ref_materiau', 'ref_mobilier', 'ref_visuel', 'photo'].includes(f.doc_type);
      if (f.doc_type === 'cctp') {
        await axios.put(`${API}/files/${f.file_id}`, { is_retained: true }, { withCredentials: true });
        toast.success('CCTP marqué pour analyse — ouverture de la matériauthèque…');
        navigate(`/materialtheque?project=${projectId}&file=${f.file_id}`);
      } else if (isImage && isVisualRef) {
        const r = await axios.post(`${API}/files/${f.file_id}/analyze-visual`, null, { withCredentials: true, timeout: 90000 });
        setFiles((prev) => prev.map((x) => x.file_id === r.data.file_id ? r.data : x));
        const tags = r.data?.ai_visual_analysis?.style_tags || [];
        toast.success(`Analyse visuelle : ${tags.slice(0,3).join(', ') || 'palette extraite'}`);
      } else {
        await axios.post(`${API}/files/${f.file_id}/categorize`, null, { withCredentials: true });
        toast.success('Analyse IA déclenchée (catégorisation)');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec analyse');
    } finally { setAnalyzingId(null); }
  }, [projectId, navigate, setFiles]);

  const handleSetTags = useCallback(async (fileId, tags) => {
    try {
      const r = await axios.put(`${API}/files/${fileId}`, { tags }, { withCredentials: true });
      setFiles((prev) => prev.map((f) => f.file_id === fileId ? r.data : f));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour tags');
    }
  }, [setFiles]);

  return (
    <HudCard variant="glass" bracket data-testid="apartment-docs-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">Documents & matériaux</span>
          <p className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}>
            {files.length} document{files.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-[11px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
            <Tag className="w-3 h-3" /> Catégorie suivante :
          </div>
          <select
            value={pendingCat}
            onChange={(e) => setPendingCat(e.target.value)}
            data-testid="apartment-docs-cat-select"
            className="h-8 px-2 rounded-md bg-white/5 border border-white/15 text-[12px] text-white"
          >
            {CATEGORIES.filter((c) => c.bucket === 'doc').map((c) => (
              <option key={c.key} value={c.key} style={{ background: '#1a1a1f' }}>{c.label}</option>
            ))}
          </select>
          <input ref={inputRef} type="file" multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*,application/pdf"
            className="hidden"
            data-testid="apartment-docs-input"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="apartment-docs-upload-btn"
            className="text-[11.5px] px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, #D9A05B 0%, #B07A35 100%)',
              color: '#0E0E10', fontWeight: 600,
              boxShadow: '0 4px 14px -4px rgba(217,160,91,0.45)',
            }}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
            Uploader
          </button>
        </div>
      </div>

      {files.length === 0 ? (
        <Dropzone onFiles={handleUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*,application/pdf"
          icon={<FileText className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.40)' }} />}
          label="Glissez vos documents ici ou cliquez sur Uploader"
          sub="CCTP, fiches matériau/mobilier, plans, autres pièces écrites — PDF/DOC/XLS/IMG"
          testid="apartment-docs-dropzone"
        />
      ) : (
        <div className="space-y-2" data-testid="apartment-docs-list">
          {files.map((f) => {
            const cat = CAT_BY_KEY[f.doc_type] || CAT_BY_KEY.other;
            const isAnalyzing = analyzingId === f.file_id;
            const visual = f.ai_visual_analysis;
            return (
              <div key={f.file_id}
                data-testid={`apartment-doc-row-${f.file_id}`}
                className="rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <cat.icon className="w-5 h-5 shrink-0" style={{ color: cat.tone }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium truncate" style={{ color: 'var(--c-text)' }}>
                      {f.original_filename || f.file_id}
                    </p>
                    <p className="text-[10.5px]" style={{ color: 'var(--c-text-muted)' }}>
                      {(f.size / 1024).toFixed(1)} KB · {new Date(f.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <select
                    value={f.doc_type || 'other'}
                    onChange={(e) => handleChangeCat(f.file_id, e.target.value)}
                    data-testid={`apartment-doc-cat-${f.file_id}`}
                    className="h-7 px-2 rounded-md bg-white/5 border border-white/15 text-[11px] text-white shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {CATEGORIES.filter((c) => c.bucket === 'doc').map((c) => (
                      <option key={c.key} value={c.key} style={{ background: '#1a1a1f' }}>{c.label}</option>
                    ))}
                  </select>
                  {/* Star toggle — shortlists this doc as a 3D generation reference (image-only) */}
                  {(f.content_type || '').startsWith('image/') && (
                    <button
                      type="button"
                      onClick={() => handleToggleStar(f.file_id, !!f.is_retained)}
                      data-testid={`apartment-doc-star-${f.file_id}`}
                      title={f.is_retained
                        ? "Étoilée ⭐ — injectée dans les rendus 3D (max 5 plus récentes par projet). Cliquer pour retirer."
                        : "Étoiler ⭐ — sera injectée comme image de référence dans la prochaine génération 3D"}
                      className="p-1 rounded shrink-0 transition-colors"
                      style={{
                        background: f.is_retained ? 'rgba(217,160,91,0.20)' : 'transparent',
                        border: `1px solid ${f.is_retained ? 'rgba(217,160,91,0.55)' : 'rgba(255,255,255,0.12)'}`,
                        color: f.is_retained ? '#FBBF24' : 'rgba(255,255,255,0.45)',
                      }}
                    >
                      <Star className="w-3.5 h-3.5" fill={f.is_retained ? 'currentColor' : 'none'} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleAnalyze(f)}
                    disabled={isAnalyzing}
                    data-testid={`apartment-doc-analyze-${f.file_id}`}
                    className="text-[11px] flex items-center gap-1 px-2 py-1 rounded shrink-0 transition-colors disabled:opacity-40"
                    style={{
                      background: 'rgba(217,160,91,0.10)',
                      border: '1px solid rgba(217,160,91,0.35)',
                      color: 'var(--c-gold)',
                    }}
                  >
                    {isAnalyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Analyser
                  </button>
                  <a
                    href={`${API}/files/${f.file_id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`apartment-doc-download-${f.file_id}`}
                    className="text-[11px] px-2 py-1 rounded shrink-0 hover:bg-white/[0.06]"
                    style={{ color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.12)' }}
                  >
                    Voir
                  </a>
                  <button
                    type="button"
                    onClick={() => handleDelete(f.file_id)}
                    data-testid={`apartment-doc-delete-${f.file_id}`}
                    className="p-1 rounded text-red-400 hover:bg-red-500/10 shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <TagsRow file={f} onSetTags={handleSetTags} />
                {visual && (visual.style_tags?.length > 0 || visual.colors?.length > 0) && (
                  <VisualAnalysisRow analysis={visual} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </HudCard>
  );
};

// ==================== SHARED SUB-COMPONENTS ====================

const TagsRow = ({ file, onSetTags }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const tags = file.tags || [];

  const handleAdd = () => {
    const t = draft.trim();
    if (!t) { setAdding(false); setDraft(''); return; }
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      toast.error('Tag déjà présent');
      return;
    }
    onSetTags(file.file_id, [...tags, t]);
    setDraft('');
    setAdding(false);
  };

  const handleRemove = (t) => onSetTags(file.file_id, tags.filter((x) => x !== t));

  if (tags.length === 0 && !adding) {
    return (
      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid={`apartment-doc-tags-add-${file.file_id}`}
          className="text-[10.5px] flex items-center gap-1 px-2 py-0.5 rounded transition-colors hover:bg-white/[0.06]"
          style={{ color: 'rgba(255,255,255,0.45)', border: '1px dashed rgba(255,255,255,0.18)' }}
        >
          <Tag className="w-2.5 h-2.5" /> + tag libre
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
      {tags.map((t) => (
        <span key={t}
          data-testid={`apartment-doc-tag-${file.file_id}-${t}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px]"
          style={{
            background: 'rgba(217,160,91,0.08)',
            border: '1px solid rgba(217,160,91,0.25)',
            color: 'var(--c-gold)',
          }}
        >
          #{t}
          <button
            type="button"
            onClick={() => handleRemove(t)}
            className="opacity-50 hover:opacity-100 transition-opacity"
            aria-label={`Supprimer tag ${t}`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}
      {adding ? (
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleAdd}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
            if (e.key === 'Escape') { setAdding(false); setDraft(''); }
          }}
          data-testid={`apartment-doc-tag-input-${file.file_id}`}
          placeholder="ajouter un tag…"
          maxLength={40}
          className="h-5 px-1.5 rounded text-[10.5px] bg-white/5 border border-white/15 text-white outline-none focus:border-[#D9A05B]/45"
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid={`apartment-doc-tags-add-${file.file_id}`}
          className="text-[10.5px] px-1.5 py-0.5 rounded hover:bg-white/[0.06] transition-colors"
          style={{ color: 'rgba(255,255,255,0.45)' }}
        >
          + tag
        </button>
      )}
    </div>
  );
};

const VisualAnalysisRow = ({ analysis }) => (
  <div className="px-3 pb-3 pt-1 border-t border-white/[0.05]" data-testid="visual-analysis-row">
    {analysis.subject && (
      <p className="text-[10.5px] mb-1.5" style={{ color: 'var(--c-text-muted)' }}>
        <span className="opacity-70">Sujet IA : </span>
        <strong style={{ color: 'var(--c-text-2)' }}>{analysis.subject}</strong>
      </p>
    )}
    {analysis.style_tags?.length > 0 && (
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        <Wand2 className="w-3 h-3" style={{ color: '#FBBF24' }} />
        {analysis.style_tags.map((t) => (
          <span key={t}
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px]"
            style={{
              background: 'rgba(251,191,36,0.08)',
              border: '1px solid rgba(251,191,36,0.25)',
              color: '#FDE68A',
            }}
          >
            {t}
          </span>
        ))}
      </div>
    )}
    {analysis.colors?.length > 0 && (
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Palette :</span>
        {analysis.colors.map((c) => (
          <span key={c}
            className="inline-flex items-center gap-1 px-1 py-0.5 rounded text-[9.5px] hud-tabular"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' }}
            title={c}
          >
            <span aria-hidden className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ background: c, border: '1px solid rgba(255,255,255,0.20)' }} />
            {c}
          </span>
        ))}
      </div>
    )}
    {analysis.materials?.length > 0 && (
      <p className="text-[10.5px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
        <span className="opacity-70">Matériaux : </span>
        {analysis.materials.join(' · ')}
      </p>
    )}
    {analysis.mood && (
      <p className="text-[10px] mt-1 italic" style={{ color: 'rgba(255,255,255,0.55)' }}>
        {analysis.mood}
      </p>
    )}
  </div>
);

const Dropzone = ({ onFiles, accept, icon, label, sub, testid }) => {
  const [drag, setDrag] = useState(false);
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); onFiles(e.dataTransfer?.files); }}
      data-testid={testid}
      className="block rounded-xl px-6 py-10 flex flex-col items-center justify-center text-center transition-all cursor-pointer"
      style={{
        background: drag ? 'rgba(217,160,91,0.10)' : 'rgba(255,255,255,0.03)',
        border: `1.5px dashed ${drag ? 'var(--c-gold)' : 'rgba(255,255,255,0.18)'}`,
      }}
    >
      {icon}
      <p className="text-[13px] font-semibold mt-3" style={{ color: 'var(--c-text)' }}>{label}</p>
      <p className="text-[11px] mt-1" style={{ color: 'var(--c-text-muted)' }}>{sub}</p>
      <input
        type="file" multiple accept={accept}
        className="hidden"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
      />
    </label>
  );
};

const Lightbox = ({ files, index, onClose, onPrev, onNext, onChangeCat }) => {
  const f = files[index];
  if (!f) return null;
  const cat = CAT_BY_KEY[f.doc_type] || CAT_BY_KEY.photo;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="apartment-photos-lightbox"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        className="absolute top-4 right-4 p-2 rounded-full"
        style={{ background: 'rgba(255,255,255,0.10)', color: 'white' }}
      >
        <X className="w-5 h-5" />
      </button>
      {files.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            data-testid="lightbox-prev"
            className="absolute left-4 p-2 rounded-full"
            style={{ background: 'rgba(255,255,255,0.10)', color: 'white' }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            data-testid="lightbox-next"
            className="absolute right-4 p-2 rounded-full"
            style={{ background: 'rgba(255,255,255,0.10)', color: 'white' }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      <div
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={`${API}/files/${f.file_id}/download`}
          alt={f.original_filename || ''}
          className="max-w-full max-h-[78vh] object-contain rounded-lg"
          crossOrigin="use-credentials"
        />
        <div className="flex items-center gap-3 px-4 py-2 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
        >
          <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
            {f.original_filename || f.file_id}
          </span>
          <select
            value={f.doc_type || 'photo'}
            onChange={(e) => onChangeCat(f.file_id, e.target.value)}
            data-testid="lightbox-cat-select"
            className="h-7 px-2 rounded bg-white/10 border border-white/15 text-[12px] text-white"
          >
            {CATEGORIES.filter((c) => c.bucket === 'photo').map((c) => (
              <option key={c.key} value={c.key} style={{ background: '#1a1a1f' }}>{c.label}</option>
            ))}
          </select>
          <span className="text-[11px] inline-flex items-center gap-1" style={{ color: cat.tone }}>
            <cat.icon className="w-3 h-3" /> {cat.label}
          </span>
          <span className="text-[10.5px] hud-tabular" style={{ color: 'var(--c-text-muted)' }}>
            {index + 1} / {files.length}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Apartment-specific blocks for the project dossier.
 *
 * These blocks are now embedded inside `ProjectSetupSection`'s Structure tab
 * when `project.project_type_code === 'apartment_single'`. This preserves
 * access to all the standard project tabs (Brief, Files, Caractéristiques,
 * Catalogue) plus the lots/pièces hierarchy, while specializing the
 * Structure tab with apartment-friendly Plan + Photos + Documents cards.
 */
export const ApartmentPlanBlock = ({ projectId, project, onProjectSaved }) => (
  <ApartmentPlanCard
    projectId={projectId}
    project={project}
    onConfirmed={(gi) => onProjectSaved?.({ general_info: gi })}
  />
);

export const ApartmentPhotosBlock = PhotosCardImpl;
export const ApartmentDocsBlock = DocsCardImpl;
export default ApartmentDossierSection;
